import { useCallback, useEffect, useRef, useState } from "react";
import { getHotspotsByImage } from "@/services/hotspots/hotspotService";

/**
 * Carrega hotspots de uma imagem 360°.
 *
 * @param {string | undefined} imageId
 * @returns {{
 *   hotspots: import("@/services/hotspots/hotspotService").Hotspot[],
 *   loading: boolean,
 *   error: string | null,
 *   refresh: () => Promise<void>,
 * }}
 */
export function useHotspots(imageId) {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(() => Boolean(imageId));
  const [error, setError] = useState(null);
  const [trackedImageId, setTrackedImageId] = useState(imageId);
  const requestedIdRef = useRef(imageId);
  requestedIdRef.current = imageId;

  if (trackedImageId !== imageId) {
    setTrackedImageId(imageId);
    setHotspots([]);
    setError(null);
    setLoading(Boolean(imageId));
  }

  const refresh = useCallback(async () => {
    const requestedId = imageId;
    if (!requestedId) {
      setHotspots([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getHotspotsByImage(requestedId);
      if (requestedIdRef.current !== requestedId) {
        return;
      }
      setHotspots(data);
    } catch (err) {
      if (requestedIdRef.current !== requestedId) {
        return;
      }
      setHotspots([]);
      setError(
        err instanceof Error
          ? err.message
          : "Não foi possível carregar os hotspots.",
      );
    } finally {
      if (requestedIdRef.current === requestedId) {
        setLoading(false);
      }
    }
  }, [imageId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { hotspots, loading, error, refresh };
}
